import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import Footer from "../Components/Footer/Footer";
import "./PerfilPage.css";

const PerfilPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout: authLogout } = useAuth();
  const { user, isLoading, error, success, fetchUserProfile, updateUserName } =
    useProfile();

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [newName, setNewName] = React.useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!user && !isLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, user, isLoading, navigate, fetchUserProfile]);

  useEffect(() => {
    if (user) {
      setNewName(user.name);
    }
  }, [user]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  const handleEditName = () => {
    setIsEditingName(true);
    setNewName(user?.name || "");
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setNewName(user?.name || "");
  };

  const handleSaveName = async () => {
    if (newName.trim() && newName !== user?.name) {
      const success = await updateUserName(newName.trim());
      if (success) {
        setIsEditingName(false);
        // Atualizar também no localStorage
        localStorage.setItem("userName", newName.trim());
      }
    } else {
      setIsEditingName(false);
    }
  };

  if (isLoading && !user) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando perfil...</p>
      </div>
    );
  }

  return (
    <div className="perfil-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton isOpen={isMenuOpen} onClick={toggleMenu} />
            )}
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu isOpen={isMenuOpen} onClose={closeMenu} />
      )}

      <main className="page-main">
        <div className="page-title">
          <h1>Perfil</h1>
          <p>Suas informações e configurações</p>
        </div>

        {error && (
          <div
            className="error-message"
            style={{
              padding: "16px 24px",
              backgroundColor: "#f44336",
              color: "#fff",
              borderRadius: "12px",
              marginBottom: "20px",
              maxWidth: "600px",
              margin: "0 auto 20px auto",
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.3)",
            }}
          >
            ❌ {error}
          </div>
        )}

        {success && (
          <div
            className="success-message"
            style={{
              padding: "16px 24px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              borderRadius: "12px",
              marginBottom: "20px",
              maxWidth: "600px",
              margin: "0 auto 20px auto",
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
            }}
          >
            ✅ Nome atualizado com sucesso!
          </div>
        )}

        <div
          className="profile-container"
          style={{
            backgroundColor: "#ffffff",
            padding: "40px",
            borderRadius: "16px",
            maxWidth: "600px",
            margin: "0 auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          }}
        >
          <h2
            style={{
              color: "#333",
              marginBottom: "30px",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            👤 Informações do Perfil
          </h2>

          {/* Email */}
          <div className="profile-field" style={{ marginBottom: "30px" }}>
            <label
              style={{
                color: "#555",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Email
            </label>
            <div
              style={{
                padding: "14px 16px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                color: "#333",
                fontSize: "16px",
                border: "1px solid #E0E0E0",
              }}
            >
              {user?.email || "carregando..."}
            </div>
            <small
              style={{
                color: "#999",
                fontSize: "12px",
                marginTop: "6px",
                display: "block",
              }}
            >
              O email não pode ser alterado
            </small>
          </div>

          {/* Nome */}
          <div className="profile-field" style={{ marginBottom: "25px" }}>
            <label
              style={{
                color: "#555",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Nome
            </label>
            {!user?.name || user?.name === "Usuário" ? (
              <small
                style={{
                  color: "#FF9800",
                  fontSize: "12px",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                ℹ️ Clique em editar para definir seu nome
              </small>
            ) : null}

            {isEditingName ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    backgroundColor: "#F5F5F5",
                    border: "2px solid #005792",
                    borderRadius: "8px",
                    color: "#333",
                    fontSize: "16px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  autoFocus
                />
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={handleSaveName}
                    disabled={isLoading}
                    style={{
                      padding: "12px 32px",
                      backgroundColor: isLoading ? "#999" : "#4CAF50",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isLoading ? "Salvando..." : "✓ Confirmar"}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                    style={{
                      padding: "12px 32px",
                      backgroundColor: "#f44336",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                  >
                    ✕ Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: "14px 16px",
                    backgroundColor: "#F5F5F5",
                    borderRadius: "8px",
                    color: "#333",
                    fontSize: "16px",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  {user?.name || "carregando..."}
                </div>
                <button
                  onClick={handleEditName}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#005792",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#004080")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#005792")
                  }
                >
                  ✏️ Editar
                </button>
              </div>
            )}
          </div>

          {/* Role */}
          <div className="profile-field">
            <label
              style={{
                color: "#555",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Tipo de Conta
            </label>
            <div
              style={{
                padding: "14px 16px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                color: "#333",
                fontSize: "16px",
                textTransform: "capitalize",
                border: "1px solid #E0E0E0",
              }}
            >
              {user?.role === "admin" ? "👑 Administrador" : "👤 Membro"}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
};

export default PerfilPage;
